import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

import { PostFilter, type PostFilterMap } from '../../../../entities/post/models';

import { useFilteredPageDateRangeSetter } from '@/shared/hooks/use-filtered-page-date-range';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

interface FilteredPostsNavigationOptions {
  filter?: Partial<PostFilterMap>;
  dimension?: PostFilter | 'default';
  dateRange?: {
    startDate: string;
    endDate: string;
  };
}

export function useFilteredPostsNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useWorkspaceId();
  const { dateRange: selectedDateRange } = useSharedDateRange();
  const setDateRange = useFilteredPageDateRangeSetter();

  return useCallback(
    async (params?: FilteredPostsNavigationOptions) => {
      const filter = params?.filter;
      const dimension = params?.dimension;
      const dateRange = params?.dateRange;
      // Start with existing search params to preserve them
      const searchParams = new URLSearchParams(location.search);

      if (!searchParams.has('dimension')) {
        searchParams.set('dimension', dimension as string);
      }

      // Set filters
      if (filter) {
        Object.entries(filter).forEach(([key, value]) => {
          if (value != null) {
            searchParams.set(key, value);
          }
        });
      }

      // Set date range only if provided
      if (dateRange) {
        setDateRange({ startDate: dateRange.startDate, endDate: dateRange.endDate }, searchParams);
      } else {
        setDateRange(
          { startDate: selectedDateRange.startDate, endDate: selectedDateRange.endDate },
          searchParams,
        );
      }

      const query = Object.fromEntries(searchParams);

      await navigate({
        to: `/workspace/${workspaceId}/insight/filtered-posts`,
        search: query,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // Intentionally not including location.search to avoid infinite loops
  );
}
